import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Award, BellRing, Eye, Loader2, Send } from "lucide-react";
import { downloadCert } from "./CertBadges";
import { usDate } from "./ui";

const STATUS = {
  certified: ["Certified", "#5B21B6", "#EDE9FE", Award],
  watched: ["Watched", "#065F46", "#D1FAE5", Eye],
  not_started: ["Not started", "#64748B", "#F1F5F9", null],
};
const bc = { borderColor: "var(--ll-border)" };

export default function TrainingBoard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const { user: me } = useAuth();
  const load = useCallback(() => api.get("/training/board").then((r) => setData(r.data)).catch(() => toast.error("Could not load training board")), []);
  useEffect(() => { load(); }, [load]);

  const remind = async (ids, key) => {
    setBusy(key);
    try {
      const r = await api.post("/training/remind", { user_ids: ids });
      const failed = r.data.email_failed.length;
      toast.success(`Reminder sent to ${r.data.reminded} ${r.data.reminded === 1 ? "person" : "people"}${failed ? ` · ${failed} email${failed === 1 ? "" : "s"} undeliverable (in-app sent)` : ""}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Reminder failed"); }
    finally { setBusy(null); }
  };
  if (!data) return <div className="p-16 grid place-items-center"><Loader2 className="animate-spin" /></div>;
  const total = data.topics.length;
  const done = data.rows.filter((r) => r.incomplete.length === 0).length;
  const pending = data.rows.filter((r) => r.incomplete.length > 0 && r.id !== me?.id).length;

  return (
    <div className="ll-card-flat overflow-hidden" data-testid="training-board">
      <div className="px-5 py-3 border-b flex flex-wrap items-center justify-between gap-3" style={bc}>
        <div>
          <div className="font-display font-bold text-lg">Advisory training progress</div>
          <div className="text-xs text-[var(--ll-ink-soft)]" data-testid="training-board-summary">
            {done} of {data.rows.length} fully certified · a video counts as watched at 90% viewed
          </div>
        </div>
        <button onClick={() => remind(null, "all")} disabled={!pending || busy}
          className="text-sm px-3 py-2 text-white bg-[var(--ll-ink)] inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="training-remind-all">
          {busy === "all" ? <Loader2 size={14} className="animate-spin" /> : <BellRing size={14} />} Remind all incomplete
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="training-board-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-white" style={{ background: "#0b5d9e" }}>
              <th className="px-5 py-3 font-medium">Team member</th>
              {data.topics.map((t) => <th key={t.key} className="px-3 py-3 font-medium">{t.title}</th>)}
              <th className="px-3 py-3 font-medium">Progress</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <BoardRow key={r.id} r={r} topics={data.topics} total={total} isMe={r.id === me?.id} busy={busy === r.id} onRemind={() => remind([r.id], r.id)} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BoardRow({ r, topics, total, isMe, busy, onRemind }) {
  const n = total - r.incomplete.length;
  return (
    <tr className="border-b hover:bg-[var(--ll-row-hover)]" style={bc} data-testid={`training-row-${r.id}`}>
      <td className="px-5 py-3">
        <div className="font-medium leading-tight">{r.name || r.email}{isMe && <span className="ml-1.5 text-[9px] uppercase tracking-widest text-[var(--ll-ink-muted)]">· you</span>}</div>
        <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">{r.role}</div>
      </td>
      {topics.map((t) => <StatusCell key={t.key} s={r.topics[t.key]} topic={t.key} userId={r.id} name={r.name} />)}
      <td className="px-3 py-3 w-36">
        <div className="h-1.5 bg-[var(--ll-mint)]"><div className="h-full bg-[#7C3AED] transition-[width] duration-700" style={{ width: `${(n / total) * 100}%` }} /></div>
        <div className="text-[11px] font-mono-fin mt-1" data-testid={`training-progress-${r.id}`}>{n}/{total} certified</div>
      </td>
      <td className="px-5 py-3 text-right whitespace-nowrap">
        {r.incomplete.length > 0 && isMe ? (
          <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]" data-testid={`training-self-${r.id}`}>Your progress</span>
        ) : r.incomplete.length > 0 ? (
          <>
            <button onClick={onRemind} disabled={busy} className="text-xs px-2.5 py-1.5 border hover:bg-[var(--ll-mint)] inline-flex items-center gap-1 disabled:opacity-50" style={bc} data-testid={`training-remind-${r.id}`}>
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send reminder
            </button>
            {r.last_reminded_at && <div className="text-[10px] text-[var(--ll-ink-muted)] mt-1" data-testid={`training-last-reminded-${r.id}`}>Reminded {usDate(r.last_reminded_at)}</div>}
          </>
        ) : <span className="text-[10px] uppercase tracking-widest font-bold text-[#5B21B6]" data-testid={`training-complete-${r.id}`}>All done</span>}
      </td>
    </tr>
  );
}

function StatusCell({ s, topic, userId, name }) {
  const [label, color, bg, Icon] = STATUS[s.status];
  const date = s.certified_at || s.watched_at;
  const chip = (
    <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 inline-flex items-center gap-1" style={{ color, background: bg }}>
      {Icon && <Icon size={10} />} {label}
    </span>
  );
  return (
    <td className="px-3 py-3" data-testid={`training-cell-${userId}-${topic}`} data-status={s.status}>
      {s.status === "certified"
        ? <button onClick={() => downloadCert(topic, userId, name)} title="Download certificate" data-testid={`training-cert-dl-${userId}-${topic}`}>{chip}</button>
        : chip}
      {date && <div className="text-[10px] text-[var(--ll-ink-muted)] mt-0.5 font-mono-fin">{usDate(date)}</div>}
    </td>
  );
}
